import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Loader2, MapPin, Phone, Mail, Globe, Star, Calendar, 
  Music, Package, ArrowLeft, CheckCircle, Clock, Instagram, Facebook, Twitter,
  Building2, Image as ImageIcon, Share2, Copy, MessageCircle, Home
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const BusinessProfile = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [systems, setSystems] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('packages');
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchBusinessData = useCallback(async () => {
    try {
      setLoading(true);
      const [businessRes, systemsRes, equipmentRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/api/businesses/${businessId}`),
        axios.get(`${API}/api/systems?business_id=${businessId}`),
        axios.get(`${API}/api/equipment?business_id=${businessId}`),
        axios.get(`${API}/api/businesses/${businessId}/reviews`).catch(() => ({ data: [] }))
      ]);
      
      setBusiness(businessRes.data);
      setSystems(Array.isArray(systemsRes.data) ? systemsRes.data : []);
      setEquipment(Array.isArray(equipmentRes.data) ? equipmentRes.data : []);
      setReviews(Array.isArray(reviewsRes.data) ? reviewsRes.data : []);
    } catch (error) {
      console.error('Failed to fetch business data:', error);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchBusinessData();
  }, [fetchBusinessData]);

  const formatReviewDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getShareUrl = () => {
    return window.location.href;
  };

  const getShareText = () => {
    return `Check out ${business?.business_name} on Gumba! ${business?.description?.slice(0, 100)}...`;
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleShareWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(getShareText() + ' ' + getShareUrl())}`;
    window.open(url, '_blank');
  };

  const handleShareFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareUrl())}`;
    window.open(url, '_blank');
  };

  const handleShareTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(getShareText())}&url=${encodeURIComponent(getShareUrl())}`;
    window.open(url, '_blank');
  };

  const renderStars = (rating) => {
    if (!rating) return <span className="text-gray-400 text-sm">No ratings yet</span>;
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-5 w-5 ${
              star <= rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'
            }`}
          />
        ))}
        <span className="text-gray-600 ml-2">({rating}) • {business?.total_ratings || 0} reviews</span>
      </div>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-yellow-500" />
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Card className="bg-gray-800 border-gray-700 p-8 text-center">
          <Building2 className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Business Not Found</h2>
          <p className="text-gray-400 mb-4">The business you&apos;re looking for doesn&apos;t exist or has been deactivated.</p>
          <Button onClick={() => navigate('/')} className="bg-yellow-500 hover:bg-yellow-600 text-black">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header/Cover Section */}
      <div className="relative h-64 md:h-80 bg-gradient-to-br from-gray-800 to-gray-700">
        {business.cover_image_url ? (
          <img 
            src={business.cover_image_url.startsWith('/') ? `${API}${business.cover_image_url}` : business.cover_image_url}
            alt={business.business_name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Music className="h-24 w-24 text-gray-600" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent" />
        
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="absolute top-4 left-4 text-white hover:bg-white/20"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        
        {/* Verified Badge */}
        {business.verification_status === 'approved' && (
          <Badge className="absolute top-4 right-4 bg-green-600 text-white">
            <CheckCircle className="h-4 w-4 mr-1" />
            Verified Business
          </Badge>
        )}
      </div>

      {/* Business Info Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative z-10">
        <div className="flex flex-col md:flex-row gap-6 items-start">
          {/* Logo */}
          <div className="w-32 h-32 rounded-xl bg-gray-800 border-4 border-gray-900 overflow-hidden flex-shrink-0">
            {business.logo_url ? (
              <img 
                src={business.logo_url.startsWith('/') ? `${API}${business.logo_url}` : business.logo_url}
                alt={`${business.business_name} logo`}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Building2 className="h-12 w-12 text-gray-600" />
              </div>
            )}
          </div>
          
          {/* Business Details */}
          <div className="flex-1">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">{business.business_name}</h1>
            <div className="mb-4">{renderStars(business.average_rating)}</div>
            <p className="text-gray-400 text-lg max-w-2xl">{business.description}</p>
          </div>
          
          {/* Share Button */}
          <div className="relative">
            <Button
              onClick={() => setShowShareMenu(!showShareMenu)}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
              data-testid="share-profile-btn"
            >
              <Share2 className="h-4 w-4 mr-2" />
              Share
            </Button>
            
            {showShareMenu && (
              <div className="absolute right-0 top-12 bg-gray-800 border border-gray-700 rounded-lg shadow-xl p-4 w-64 z-50">
                <h4 className="text-white font-semibold mb-3">Share this business</h4>
                <div className="space-y-2">
                  <button
                    onClick={handleShareWhatsApp}
                    className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-700 text-gray-300 hover:text-white transition-colors"
                    data-testid="share-whatsapp-btn"
                  >
                    <MessageCircle className="h-5 w-5 text-green-500" />
                    Share on WhatsApp
                  </button>
                  <button
                    onClick={handleShareFacebook}
                    className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-700 text-gray-300 hover:text-white transition-colors"
                    data-testid="share-facebook-btn"
                  >
                    <Facebook className="h-5 w-5 text-blue-500" />
                    Share on Facebook
                  </button>
                  <button
                    onClick={handleShareTwitter}
                    className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-700 text-gray-300 hover:text-white transition-colors"
                    data-testid="share-twitter-btn"
                  >
                    <Twitter className="h-5 w-5 text-sky-500" />
                    Share on X (Twitter)
                  </button>
                  <div className="border-t border-gray-700 pt-2 mt-2">
                    <button
                      onClick={handleCopyLink}
                      className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-700 text-gray-300 hover:text-white transition-colors"
                      data-testid="copy-link-btn"
                    >
                      <Copy className="h-5 w-5 text-yellow-500" />
                      {copied ? 'Copied!' : 'Copy Link'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Contact & Info Cards */}
        <div className="grid md:grid-cols-3 gap-6 mt-8">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-yellow-500 mt-1" />
                <div>
                  <h3 className="font-semibold text-white mb-1">Location</h3>
                  <p className="text-gray-400 text-sm">
                    {business.address?.street}<br />
                    {business.address?.city}, {business.address?.province}<br />
                    {business.address?.postal_code}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-yellow-500 mt-1" />
                <div>
                  <h3 className="font-semibold text-white mb-1">Contact</h3>
                  <p className="text-gray-400 text-sm">{business.phone}</p>
                  <p className="text-gray-400 text-sm">{business.email}</p>
                  {business.website && (
                    <a href={business.website} target="_blank" rel="noopener noreferrer" 
                       className="text-yellow-500 text-sm hover:underline flex items-center gap-1 mt-1">
                      <Globe className="h-3 w-3" /> Website
                    </a>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-yellow-500 mt-1" />
                <div>
                  <h3 className="font-semibold text-white mb-1">Stats</h3>
                  <p className="text-gray-400 text-sm">{business.total_bookings || 0} bookings completed</p>
                  <p className="text-gray-400 text-sm">{systems.length} PA systems</p>
                  <p className="text-gray-400 text-sm">{equipment.length} equipment items</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Social Links */}
        {(business.facebook || business.instagram || business.twitter) && (
          <div className="flex gap-4 mt-6">
            {business.facebook && (
              <a href={business.facebook} target="_blank" rel="noopener noreferrer"
                 className="w-10 h-10 bg-gray-800 hover:bg-yellow-500 rounded-full flex items-center justify-center transition-colors group">
                <Facebook className="h-5 w-5 text-gray-400 group-hover:text-black" />
              </a>
            )}
            {business.instagram && (
              <a href={business.instagram} target="_blank" rel="noopener noreferrer"
                 className="w-10 h-10 bg-gray-800 hover:bg-yellow-500 rounded-full flex items-center justify-center transition-colors group">
                <Instagram className="h-5 w-5 text-gray-400 group-hover:text-black" />
              </a>
            )}
            {business.twitter && (
              <a href={business.twitter} target="_blank" rel="noopener noreferrer"
                 className="w-10 h-10 bg-gray-800 hover:bg-yellow-500 rounded-full flex items-center justify-center transition-colors group">
                <Twitter className="h-5 w-5 text-gray-400 group-hover:text-black" />
              </a>
            )}
          </div>
        )}

        {/* Reviews Section - Show if business has ratings */}
        {(business.total_ratings > 0 || reviews.length > 0) && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <Star className="h-6 w-6 text-yellow-500" />
              Customer Reviews ({business.total_ratings || reviews.length})
            </h2>
            
            {/* Rating Summary Cards */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-5xl font-bold text-yellow-500 mb-2">
                      {business.average_rating?.toFixed(1) || '-'}
                    </div>
                    <div className="flex justify-center mb-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`h-6 w-6 ${
                            star <= (business.average_rating || 0) 
                              ? 'text-yellow-500 fill-yellow-500' 
                              : 'text-gray-600'
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-gray-400">Based on {business.total_ratings} reviews</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-white mb-4">Rating Breakdown</h3>
                  {[5, 4, 3, 2, 1].map((stars) => (
                    <div key={stars} className="flex items-center gap-2 mb-2">
                      <span className="text-sm text-gray-400 w-12">{stars} star</span>
                      <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-yellow-500 rounded-full"
                          style={{ 
                            width: `${Math.round((business.average_rating || 0) === stars ? 100 : 
                                    Math.abs(stars - (business.average_rating || 0)) < 1 ? 50 : 10)}%` 
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            {/* Individual Reviews */}
            {reviews.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">Recent Reviews</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {reviews.map((review) => (
                    <Card key={review.booking_id} className="bg-gray-800 border-gray-700">
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-medium text-white">{review.reviewer_name}</p>
                            <p className="text-xs text-gray-500">{formatReviewDate(review.rated_at)}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`h-4 w-4 ${
                                  star <= review.rating 
                                    ? 'text-yellow-500 fill-yellow-500' 
                                    : 'text-gray-600'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        {review.review && (
                          <p className="text-gray-300 text-sm mb-2">{review.review}</p>
                        )}
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span className="px-2 py-1 bg-gray-700 rounded">{review.service_type}</span>
                          {review.service_name && (
                            <span className="truncate">{review.service_name}</span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Equipment Tabs */}
        <div className="mt-12 pb-12">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full max-w-md grid-cols-2 bg-gray-800 mb-8">
              <TabsTrigger value="packages" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
                <Music className="h-4 w-4 mr-2" />
                PA Systems ({systems.length})
              </TabsTrigger>
              <TabsTrigger value="equipment" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
                <Package className="h-4 w-4 mr-2" />
                Equipment ({equipment.length})
              </TabsTrigger>
            </TabsList>
            
            {/* PA Systems Tab */}
            <TabsContent value="packages">
              {systems.length === 0 ? (
                <Card className="bg-gray-800 border-gray-700 p-8 text-center">
                  <Music className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">No PA systems available from this vendor yet.</p>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {systems.map((system) => (
                    <Card 
                      key={system.system_id} 
                      className="bg-gray-800 border-gray-700 hover:border-yellow-500 transition-all cursor-pointer overflow-hidden"
                      onClick={() => navigate(`/system/${system.system_id}`)}
                    >
                      <div className="aspect-video relative">
                        <img 
                          src={system.image?.startsWith('/') ? `${API}${system.image}` : system.image}
                          alt={system.name}
                          className="w-full h-full object-cover"
                        />
                        <Badge className="absolute top-3 right-3 bg-yellow-500 text-black">
                          {system.category}
                        </Badge>
                      </div>
                      <CardContent className="pt-4">
                        <h3 className="text-lg font-semibold text-white mb-2">{system.name}</h3>
                        <p className="text-gray-400 text-sm line-clamp-2 mb-3">{system.description}</p>
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-2xl font-bold text-yellow-500">{formatCurrency(system.price)}</span>
                            <span className="text-gray-400 text-sm">/{system.price_unit}</span>
                          </div>
                          <Button 
                            className="bg-yellow-500 hover:bg-yellow-600 text-black"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/book/${system.system_id}?business=${businessId}`);
                            }}
                          >
                            Book Now
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
            
            {/* Equipment Tab */}
            <TabsContent value="equipment">
              {equipment.length === 0 ? (
                <Card className="bg-gray-800 border-gray-700 p-8 text-center">
                  <Package className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">No equipment available from this vendor yet.</p>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {equipment.map((item) => (
                    <Card 
                      key={item.equipment_id} 
                      className="bg-gray-800 border-gray-700 hover:border-yellow-500 transition-all cursor-pointer overflow-hidden"
                      onClick={() => navigate(`/equipment/${item.equipment_id}`)}
                    >
                      <div className="aspect-video relative">
                        <img 
                          src={item.image?.startsWith('/') ? `${API}${item.image}` : item.image}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                        <Badge className="absolute top-3 right-3 bg-blue-500 text-white">
                          {item.category}
                        </Badge>
                        {item.quantity_available > 1 && (
                          <Badge className="absolute top-3 left-3 bg-gray-900/80 text-white">
                            {item.quantity_available} available
                          </Badge>
                        )}
                      </div>
                      <CardContent className="pt-4">
                        <h3 className="text-lg font-semibold text-white mb-2">{item.name}</h3>
                        <p className="text-gray-400 text-sm line-clamp-2 mb-3">{item.description}</p>
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-2xl font-bold text-yellow-500">{formatCurrency(item.price)}</span>
                            <span className="text-gray-400 text-sm">/{item.price_unit}</span>
                          </div>
                          <Button 
                            className="bg-yellow-500 hover:bg-yellow-600 text-black"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/equipment/${item.equipment_id}/book?business=${businessId}`);
                            }}
                          >
                            Book Now
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Instagram Gallery Section - After Equipment Tabs */}
        {business.instagram_handle && (
          <div className="pb-12">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <Instagram className="h-6 w-6 text-yellow-500" />
              Our Work on Instagram
            </h2>
            <Card className="bg-gray-800 border-gray-700 overflow-hidden">
              <CardContent className="p-6">
                {/* Instagram Profile Header - Compact */}
                <div className="flex items-center gap-4 mb-6 pb-4 border-b border-gray-700">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-yellow-500 p-[2px] flex-shrink-0">
                    <div className="w-full h-full rounded-full bg-gray-900 flex items-center justify-center">
                      <Instagram className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white">@{business.instagram_handle}</h3>
                    <p className="text-gray-400 text-sm">See our latest events and setups</p>
                  </div>
                  <a 
                    href={`https://instagram.com/${business.instagram_handle}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 via-pink-500 to-yellow-500 text-white font-medium px-4 py-2 rounded-lg hover:opacity-90 transition-opacity text-sm"
                  >
                    <Instagram className="h-4 w-4" />
                    Follow
                  </a>
                </div>
                
                {/* Instagram Embed - 50% scaled */}
                <div className="flex justify-center">
                  <div className="w-full max-w-md transform scale-100 origin-top">
                    <iframe
                      src={`https://www.instagram.com/${business.instagram_handle}/embed`}
                      className="w-full h-[320px] bg-gray-900 rounded-lg"
                      frameBorder="0"
                      scrolling="yes"
                      title={`${business.business_name} Instagram`}
                    />
                  </div>
                </div>
                
                <p className="text-center text-gray-500 text-xs mt-4">
                  Scroll within the frame to see more posts
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessProfile;
